import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-image-add-line',
    color: '#00d4ff',
    title: 'Send Your Ad',
    desc: 'Submit your creative — image, video, or let our design team craft a stunning LED-optimized ad for your brand.',
  },
  {
    number: '02',
    icon: 'ri-calendar-check-line',
    color: '#39ff14',
    title: 'Choose Your Campaign',
    desc: 'Pick monthly, hourly, or event-based packages. Select your target routes, neighborhoods, and exposure windows.',
  },
  {
    number: '03',
    icon: 'ri-truck-line',
    color: '#b026ff',
    title: 'We Hit the Streets',
    desc: 'Our LED truck rolls through Tampa displaying your ad 24/7 — high-traffic intersections, events, and everywhere your customers are.',
  },
  {
    number: '04',
    icon: 'ri-bar-chart-box-line',
    color: '#ff073a',
    title: 'You Get Exposure',
    desc: 'Watch your brand recognition explode. Thousands of real Tampa locals see your ad every single day.',
  },
];

const HowItWorks = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="relative py-24 md:py-32 bg-dark-base overflow-hidden" ref={sectionRef}>
      {/* BG Grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(57,255,20,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.5) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div
            className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: '#b026ff', border: '1px solid #b026ff', boxShadow: '0 0 10px rgba(176,38,255,0.3)' }}
          >
            The Process
          </div>
          <h2
            className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl"
            style={{ color: '#ffffff', textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            HOW IT{' '}
            <span style={{ color: '#00d4ff', textShadow: '0 0 20px #00d4ff' }}>WORKS</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg font-rajdhani">
            Four simple steps to street domination
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`relative group p-6 md:p-8 rounded-3xl bg-dark-card cursor-pointer transition-all duration-500 hover:scale-103 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{
                borderTop: `4px solid ${step.color}`,
                border: `1px solid ${step.color}20`,
                borderTopWidth: '4px',
                transitionDelay: `${i * 150}ms`,
                boxShadow: `0 0 0 transparent`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${step.color}30`;
                (e.currentTarget as HTMLDivElement).style.borderColor = `${step.color}60`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLDivElement).style.borderColor = `${step.color}20`;
              }}
            >
              {/* Connector Line for large screens */}
              {i < 3 && (
                <div
                  className="hidden lg:block absolute top-12 -right-3 w-6 h-px z-10"
                  style={{ background: `linear-gradient(90deg, ${step.color}, transparent)`, boxShadow: `0 0 5px ${step.color}` }}
                />
              )}

              {/* Step Number */}
              <div
                className="w-14 h-14 flex items-center justify-center rounded-full mb-6 font-orbitron font-black text-xl"
                style={{
                  backgroundColor: `${step.color}15`,
                  color: step.color,
                  boxShadow: `0 0 15px ${step.color}30`,
                }}
              >
                {step.number}
              </div>

              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center mb-5">
                <i
                  className={`${step.icon} text-4xl group-hover:scale-110 transition-transform duration-300`}
                  style={{ color: step.color, textShadow: `0 0 20px ${step.color}` }}
                />
              </div>

              <h3
                className="font-rajdhani font-bold text-xl md:text-2xl mb-3"
                style={{ color: step.color }}
              >
                {step.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-14 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-rajdhani font-bold tracking-widest uppercase text-base text-black transition-all duration-300 hover:scale-105 whitespace-nowrap"
            style={{ backgroundColor: '#39ff14', boxShadow: '0 0 30px rgba(57,255,20,0.5)' }}
          >
            <i className="ri-flashlight-line" />
            Start Your Campaign Now
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
